import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Truck, Plus, X, Globe, Package, Pencil, Trash2, Search, ExternalLink, ShoppingCart } from 'lucide-react';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Can, useCan } from '../../lib/rbac';

interface Supplier {
  id: number;
  name: string;
  type: string;
  country: string;
  website: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  payment_terms: string | null;
  lead_time_days: number | null;
  api_connector: string | null;
  status: string;
  notes: string | null;
  metadata: Record<string, unknown>;
  tenant_id: number;
  product_count: number;
  created_at: string;
  updated_at: string;
}

interface SupplierStats {
  total: number;
  active: number;
  inactive: number;
  with_api: number;
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '\u{1F1FA}\u{1F1F8}',
  MX: '\u{1F1F2}\u{1F1FD}',
  AR: '\u{1F1E6}\u{1F1F7}',
  BR: '\u{1F1E7}\u{1F1F7}',
  CL: '\u{1F1E8}\u{1F1F1}',
  CO: '\u{1F1E8}\u{1F1F4}',
  UY: '\u{1F1FA}\u{1F1FE}',
  CN: '\u{1F1E8}\u{1F1F3}',
  DE: '\u{1F1E9}\u{1F1EA}',
  ES: '\u{1F1EA}\u{1F1F8}',
  GB: '\u{1F1EC}\u{1F1E7}',
  JP: '\u{1F1EF}\u{1F1F5}',
  KR: '\u{1F1F0}\u{1F1F7}',
  IT: '\u{1F1EE}\u{1F1F9}',
  FR: '\u{1F1EB}\u{1F1F7}',
  CA: '\u{1F1E8}\u{1F1E6}',
  IN: '\u{1F1EE}\u{1F1F3}',
  PE: '\u{1F1F5}\u{1F1EA}',
  EC: '\u{1F1EA}\u{1F1E8}',
  PA: '\u{1F1F5}\u{1F1E6}',
};

function countryFlag(country: string): string {
  if (!country) return '';
  const code = country.trim().toUpperCase();
  if (COUNTRY_FLAGS[code]) return COUNTRY_FLAGS[code];
  // Try to generate flag from 2-letter code
  if (code.length === 2) {
    return String.fromCodePoint(
      0x1F1E6 + code.charCodeAt(0) - 65,
      0x1F1E6 + code.charCodeAt(1) - 65
    );
  }
  return '';
}

const TYPE_COLORS: Record<string, string> = {
  marketplace: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  distributor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  manufacturer: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  wholesaler: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
};

const STATUS_MAP: Record<string, 'ok' | 'unknown'> = {
  active: 'ok',
  inactive: 'unknown',
};

// Re-export as embeddable panel (used by PurchasesPage tab)
export { SuppliersPage as SuppliersPanel };

export default function SuppliersPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [stats, setStats] = useState<SupplierStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [suppliersRes, statsRes] = await Promise.all([
      api.get<Supplier[]>('/suppliers'),
      api.get<SupplierStats>('/suppliers/stats'),
    ]);
    if (suppliersRes.success && suppliersRes.data) setSuppliers(suppliersRes.data);
    if (statsRes.success && statsRes.data) setStats(statsRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredSuppliers = suppliers.filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        (s.contact_name || '').toLowerCase().includes(q) ||
        (s.contact_email || '').toLowerCase().includes(q) ||
        (s.api_connector || '').toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        s.country.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleDelete = async (id: number) => {
    await api.delete(`/suppliers/${id}`);
    setDeleteConfirmId(null);
    if (selectedSupplier?.id === id) setSelectedSupplier(null);
    loadData();
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSupplier(null);
  };

  const handleFormSave = async (data: Partial<Supplier>) => {
    if (editingSupplier) {
      await api.put(`/suppliers/${editingSupplier.id}`, data);
    } else {
      await api.post('/suppliers', data);
    }
    handleFormClose();
    loadData();
  };

  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleDateString() : '';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('suppliers.title')}</h1>
        <Can cap="purchases.suppliers_manage">
          <button
            onClick={() => { setEditingSupplier(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90"
          >
            <Plus size={16} /> {t('suppliers.addSupplier')}
          </button>
        </Can>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: t('suppliers.totalSuppliers'), value: stats.total, color: 'var(--color-primary)' },
            { label: t('suppliers.active'), value: stats.active, color: 'var(--color-success)' },
            { label: t('suppliers.inactive'), value: stats.inactive, color: 'var(--color-text-muted)' },
            { label: t('suppliers.withApi'), value: stats.with_api, color: 'var(--color-primary)' },
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder={t('suppliers.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
        >
          <option value="all">{t('suppliers.allStatuses')}</option>
          <option value="active">{t('suppliers.active')}</option>
          <option value="inactive">{t('suppliers.inactive')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="animate-pulse p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-[var(--color-border-light)] rounded" />)}
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <Truck size={48} className="opacity-20 mb-3" />
            <p>{t('suppliers.noSuppliers')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.name')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.type')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.country')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.contactName')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.apiConnector')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('suppliers.products')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.status')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map(s => (
                  <tr
                    key={s.id}
                    className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] cursor-pointer"
                    onClick={() => setSelectedSupplier(s)}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-sm text-[var(--color-text)]">{s.name}</div>
                      {s.website && (
                        <div className="text-xs text-[var(--color-text-muted)] truncate max-w-[200px]">{s.website.replace(/^https?:\/\//, '')}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[s.type] || TYPE_COLORS.other}`}>
                        {t(`suppliers.${s.type}`, s.type)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-sm">
                      {s.country ? (
                        <span>{countryFlag(s.country)} {s.country}</span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">--</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {s.contact_name ? (
                        <div>
                          <div className="text-sm text-[var(--color-text)]">{s.contact_name}</div>
                          {s.contact_email && <div className="text-xs text-[var(--color-text-muted)]">{s.contact_email}</div>}
                        </div>
                      ) : (
                        <span className="text-sm text-[var(--color-text-muted)]">--</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {s.api_connector ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-success-light)] text-[var(--color-success)]">
                          <Globe size={10} />
                          {s.api_connector}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--color-text-muted)]">--</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-mono">
                      {s.product_count > 0 ? s.product_count.toLocaleString() : <span className="text-[var(--color-text-muted)]">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <StatusBadge status={STATUS_MAP[s.status] || 'unknown'} label={s.status} size="sm" />
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Can cap="purchases.suppliers_manage">
                          <button
                            onClick={e => { e.stopPropagation(); handleEdit(s); }}
                            className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]"
                            title={t('common.edit')}
                          >
                            <Pencil size={14} className="text-[var(--color-text-muted)]" />
                          </button>
                        </Can>
                        {s.api_connector !== 'amazon' && can('purchases.delete') && (
                          <button
                            onClick={e => { e.stopPropagation(); setDeleteConfirmId(s.id); }}
                            className="p-1.5 rounded hover:bg-[var(--color-error-light)]"
                            title={t('common.delete')}
                          >
                            <Trash2 size={14} className="text-[var(--color-error)]" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Table footer */}
        {!loading && filteredSuppliers.length > 0 && (
          <div className="px-4 py-3 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)]">
            {filteredSuppliers.length} {t('suppliers.suppliersCount')}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirmId !== null && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setDeleteConfirmId(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-[var(--color-surface)] rounded-xl shadow-2xl z-50 p-5">
            <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">{t('common.confirm')}</h3>
            <p className="text-sm text-[var(--color-text-muted)] mb-4">{t('suppliers.deleteConfirm')}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="px-4 py-2 rounded-lg bg-[var(--color-error)] text-white text-sm font-medium hover:opacity-90"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Detail panel */}
      {selectedSupplier && (
        <SupplierDetailPanel
          supplier={selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
          onEdit={() => handleEdit(selectedSupplier)}
        />
      )}

      {/* Form modal */}
      {showForm && (
        <SupplierForm
          supplier={editingSupplier}
          onSave={handleFormSave}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// -- Detail Panel --

function SupplierDetailPanel({
  supplier: s,
  onClose,
  onEdit,
}: {
  supplier: Supplier;
  onClose: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    if (!s.api_connector) return;
    setLoadingPurchases(true);
    // Load recent amazon purchases for suppliers with API connector
    api.get<any[]>(`/purchases/amazon?per_page=10&seller_id=${s.tenant_id}`).then(res => {
      if (res.success && res.data) setPurchases(res.data);
      setLoadingPurchases(false);
    }).catch(() => setLoadingPurchases(false));
  }, [s.id, s.api_connector, s.tenant_id]);

  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleDateString() : '--';
  const fmt = (v: any) => (canMoney && v != null ? `$${Number(v).toFixed(2)}` : '--');

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-[var(--color-surface)] shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[var(--color-text)]">{t('suppliers.detail')}</h2>
          <div className="flex items-center gap-2">
            <Can cap="purchases.suppliers_manage">
              <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
                <Pencil size={18} className="text-[var(--color-text-muted)]" />
              </button>
            </Can>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--color-primary-light)] flex items-center justify-center">
              <Truck size={24} className="text-[var(--color-primary)]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[var(--color-text)]">{s.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={STATUS_MAP[s.status] || 'unknown'} label={s.status} size="sm" />
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[s.type] || TYPE_COLORS.other}`}>
                  {t(`suppliers.${s.type}`, s.type)}
                </span>
                {s.country && <span className="text-sm">{countryFlag(s.country)} {s.country}</span>}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <InfoCard label={t('suppliers.products')} value={s.product_count.toLocaleString()} icon={<Package size={16} className="text-[var(--color-primary)]" />} />
            <InfoCard
              label={t('suppliers.apiConnector')}
              value={s.api_connector ? s.api_connector.toUpperCase() : t('suppliers.notConnected')}
              valueColor={s.api_connector ? 'var(--color-success)' : undefined}
              icon={<Globe size={16} className="text-[var(--color-text-muted)]" />}
            />
          </div>

          {/* Contact info */}
          {(s.contact_name || s.contact_email || s.contact_phone) && (
            <DetailSection title={t('suppliers.contactName')}>
              <div className="space-y-1.5 text-sm">
                {s.contact_name && <div className="font-medium text-[var(--color-text)]">{s.contact_name}</div>}
                {s.contact_email && (
                  <a href={`mailto:${s.contact_email}`} className="block text-[var(--color-primary)] hover:underline">{s.contact_email}</a>
                )}
                {s.contact_phone && <div className="text-[var(--color-text-muted)]">{s.contact_phone}</div>}
              </div>
            </DetailSection>
          )}

          {/* Business details */}
          <DetailSection title={t('suppliers.businessDetails')}>
            <div className="space-y-2 text-sm">
              {s.website && (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-text-muted)]">{t('suppliers.website')}</span>
                  <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] hover:underline flex items-center gap-1 truncate ml-4">
                    {s.website.replace(/^https?:\/\//, '')}
                    <ExternalLink size={12} />
                  </a>
                </div>
              )}
              {s.payment_terms && (
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">{t('suppliers.paymentTerms')}</span>
                  <span className="text-[var(--color-text)]">{s.payment_terms}</span>
                </div>
              )}
              {s.lead_time_days != null && (
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">{t('suppliers.leadTime')}</span>
                  <span className="text-[var(--color-text)]">{s.lead_time_days} {t('suppliers.leadTimeDays')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--color-text-muted)]">{t('catalog.created')}</span>
                <span className="text-[var(--color-text)]">{fmtDate(s.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-text-muted)]">{t('catalog.updated')}</span>
                <span className="text-[var(--color-text)]">{fmtDate(s.updated_at)}</span>
              </div>
            </div>
          </DetailSection>

          {/* Notes */}
          {s.notes && (
            <DetailSection title={t('suppliers.notes')}>
              <div className="text-sm text-[var(--color-text-muted)] whitespace-pre-wrap">{s.notes}</div>
            </DetailSection>
          )}

          {/* Recent purchases */}
          {s.api_connector && (
            <DetailSection title={t('suppliers.recentPurchases')}>
              {loadingPurchases ? (
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-10 bg-[var(--color-border-light)] rounded" />)}
                </div>
              ) : purchases.length === 0 ? (
                <div className="flex flex-col items-center py-4 text-[var(--color-text-muted)]">
                  <ShoppingCart size={24} className="opacity-20 mb-1" />
                  <span className="text-xs">{t('purchases.noData')}</span>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {purchases.map((p: any) => (
                    <div key={p.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--color-surface-hover)] text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-[var(--color-text)]">{p.asin}</span>
                        <StatusBadge
                          status={p.amazon_status === 'delivered' || p.amazon_status === 'delivered_ml' ? 'ok' : p.amazon_status === 'error' || p.amazon_status === 'cancelled' ? 'error' : 'info'}
                          label={p.amazon_status}
                          size="sm"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono">{fmt(p.total_cost)}</span>
                        <span className="text-[var(--color-text-muted)]">{fmtDate(p.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          )}
        </div>
      </div>
    </>
  );
}

function InfoCard({ label, value, valueColor, icon }: { label: string; value: string; valueColor?: string; icon?: React.ReactNode }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)] text-center">
      {icon && <div className="flex justify-center mb-1">{icon}</div>}
      <div className="text-lg font-bold" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-2">{title}</h4>
      <div className="bg-[var(--color-bg)] rounded-lg border border-[var(--color-border-light)] p-3">
        {children}
      </div>
    </div>
  );
}

// -- Supplier Form (modal) --

function SupplierForm({
  supplier,
  onSave,
  onClose,
}: {
  supplier: Supplier | null;
  onSave: (data: Partial<Supplier>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: supplier?.name || '',
    type: supplier?.type || 'other',
    country: supplier?.country || '',
    website: supplier?.website || '',
    contact_name: supplier?.contact_name || '',
    contact_email: supplier?.contact_email || '',
    contact_phone: supplier?.contact_phone || '',
    payment_terms: supplier?.payment_terms || '',
    lead_time_days: supplier?.lead_time_days ?? '',
    api_connector: supplier?.api_connector || '',
    status: supplier?.status || 'active',
    notes: supplier?.notes || '',
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!form.name.trim()) newErrors.name = true;
    if (!form.type) newErrors.type = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      ...form,
      lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null,
      website: form.website || null,
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      payment_terms: form.payment_terms || null,
      api_connector: form.api_connector || null,
      notes: form.notes || null,
    } as Partial<Supplier>);
  };

  const field = (key: keyof typeof form, label: string, type = 'text', required = false) => (
    <div>
      <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
        {label} {required && <span className="text-[var(--color-error)]">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); if (errors[key]) setErrors(er => ({ ...er, [key]: false })); }}
        className={`w-full px-3 py-2 rounded-lg border ${errors[key] ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'} bg-[var(--color-bg)] text-sm text-[var(--color-text)]`}
      />
      {errors[key] && <span className="text-xs text-[var(--color-error)]">{t('suppliers.fieldRequired')}</span>}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[var(--color-surface)] rounded-xl shadow-2xl z-50 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--color-text)]">
            {supplier ? t('common.edit') + ' ' + t('suppliers.title').toLowerCase() : t('suppliers.addSupplier')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {field('name', t('suppliers.name'), 'text', true)}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                {t('suppliers.type')} <span className="text-[var(--color-error)]">*</span>
              </label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border ${errors.type ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'} bg-[var(--color-bg)] text-sm text-[var(--color-text)]`}
              >
                <option value="marketplace">{t('suppliers.marketplace')}</option>
                <option value="distributor">{t('suppliers.distributor')}</option>
                <option value="manufacturer">{t('suppliers.manufacturer')}</option>
                <option value="wholesaler">{t('suppliers.wholesaler')}</option>
                <option value="other">{t('suppliers.other')}</option>
              </select>
            </div>
            {field('country', t('suppliers.country'))}
          </div>

          {field('website', t('suppliers.website'), 'url')}

          <div className="grid grid-cols-2 gap-3">
            {field('contact_name', t('suppliers.contactName'))}
            {field('contact_email', t('suppliers.contactEmail'), 'email')}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {field('contact_phone', t('suppliers.contactPhone'), 'tel')}
            {field('lead_time_days', t('suppliers.leadTime'), 'number')}
          </div>

          {field('payment_terms', t('suppliers.paymentTerms'))}
          {field('api_connector', t('suppliers.apiConnector'))}

          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">{t('suppliers.status')}</label>
            <select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)]"
            >
              <option value="active">{t('suppliers.active')}</option>
              <option value="inactive">{t('suppliers.inactive')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">{t('suppliers.notes')}</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)]"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]">
              {t('common.cancel')}
            </button>
            <button type="submit"
              className="px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90">
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
